import { lazy, Suspense } from 'react';
import { Navigate, Outlet, redirect, type LoaderFunction, type RouteObject } from 'react-router';
import { Spin } from 'antd';
import { ProtectedRoutes } from '@/router/ProtectedRoutes.tsx';
import { routes } from '@/router/routes.tsx';
import type { ModuleFunction } from '@/features/auth/auth.def.ts';
import { investorDashboardAnalyticsRoute } from '@/pages/investor-management';
import { navBreakdownAnalyticsRoute } from '@/pages/reports';
import { fundDashboardAnalyticsRoute } from '@/pages/fund-management';

const AnalyticsPage = lazy(() => import('./analytics-page.tsx'));

/** UX gate only; the reports and funds controllers' attributes are the security boundary. */
const READ_INVESTOR_PROFILE: ModuleFunction[] = [
  { moduleName: 'investor-management', functionName: 'read-investor-profile' },
];

/**
 * Sends a legacy URL to its canonical tab, copying the query string (all scope dimensions) verbatim.
 * A loader rather than a component, so the redirect replaces the history entry before any render.
 */
const legacyAnalyticsRedirect =
  (to: string): LoaderFunction =>
  ({ request }) =>
    redirect(`${to}${new URL(request.url).search}`);

const analyticsTabRoutes: RouteObject = {
  path: 'analytics',
  element: (
    <Suspense fallback={<Spin />}>
      <AnalyticsPage />
    </Suspense>
  ),
  children: [
    { index: true, element: <Navigate to="investors" replace /> },
    investorDashboardAnalyticsRoute,
    {
      element: <ProtectedRoutes requiredPermissions={READ_INVESTOR_PROFILE} />,
      children: [navBreakdownAnalyticsRoute],
    },
    {
      element: <ProtectedRoutes requiredPermissions={READ_INVESTOR_PROFILE} />,
      children: [fundDashboardAnalyticsRoute],
    },
  ],
};

/**
 * Canonical Analytics tabs plus the legacy dashboard/report URLs as redirects. Mounted before the
 * feature route modules so these legacy registrations win over the modules' own children.
 */
export const analyticsRoutes: RouteObject = {
  element: <Outlet />,
  children: [
    analyticsTabRoutes,
    {
      path: 'investor-management/dashboard',
      loader: legacyAnalyticsRedirect(routes.analyticsInvestors),
    },
    {
      path: 'reports/nav-breakdown',
      loader: legacyAnalyticsRedirect(routes.analyticsNavBreakdown),
    },
    {
      path: 'fund-management/dashboard',
      loader: legacyAnalyticsRedirect(routes.analyticsFunds),
    },
  ],
};

export default analyticsRoutes;
